import { apiClient } from './api';

export type HealthPeriod = 'today' | 'yesterday' | '7d' | '30d';

export const HEALTH_PERIOD_OPTIONS: Array<{ value: HealthPeriod; label: string }> = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
];

export interface PhoneHealthSummary {
  total: number;
  healthy: number;
  warning: number;
  replace: number;
  warmUp: number;
  avgConnectRate: number;
  avgDeliveryRate: number;
  carrierSpamIssues: number;
  poolDailyCapacity: number;
  poolUsedToday: number;
  poolRemainingToday: number;
  poolUtilizationPercent: number;
  poolCallableNumbers: number;
  period?: HealthPeriod;
  periodLabel?: string;
  periodDays?: number;
}

export interface PhoneHealthListItem {
  id: string;
  phoneNumber: string;
  areaCode: string | null;
  timezone: string | null;
  usageType: string | null;
  purpose: string | null;
  purposeLabel: string;
  healthStatus: string;
  healthStatusSince: string | null;
  healthStatusManual?: boolean;
  healthManualCallsRemaining?: number | null;
  warmupDay: number;
  dailyCallLimit: number | null;
  dailyTextLimit: number | null;
  effectiveDailyCallLimit: number;
  callsToday: number;
  utilizationTodayPercent: number | null;
  textsToday: number;
  lastUsedAt: string | null;
  connectRate: number | null;
  deliveryRate: number | null;
  stopRate: number | null;
  callsMade: number;
  callsAnswered: number;
  textsSent: number;
  textsDelivered: number;
  carrierIssues: string[];
  campaigns: Array<{ id: string; name: string }>;
  provider: string;
  connectionName: string | null;
  smsCapable: boolean;
  volumeMultiplier: number;
}

export interface AreaCodeGapRow {
  areaCode: string;
  timezone: string;
  totalCallableLeads: number;
  activeNumbersOwned: number;
  numbersInWarmup: number;
  numbersWarning: number;
  numbersReplace: number;
  dailyCapacity: number;
  usedCapacityToday: number;
  remainingCapacity: number;
  utilizationPercent: number;
  requiredNumbers: number;
  missingNumbers: number;
  suggestedPurchaseCount: number | null;
  status: string;
}

export interface ReplacementLogEntry {
  id: string;
  old_number: string;
  new_number: string;
  area_code: string | null;
  timezone: string | null;
  usage_type: string | null;
  reason_replaced: string | null;
  replaced_by: string;
  replacement_date: string;
}

export const phoneNumberHealthService = {
  async getSummary(period: HealthPeriod = 'today'): Promise<PhoneHealthSummary> {
    const res = await apiClient.get<PhoneHealthSummary>(
      `/phone-numbers/health/summary?period=${period}`,
      { skipErrorToast: true }
    );
    return res.data.data;
  },

  async getByTimezone() {
    const res = await apiClient.get('/phone-numbers/health/by-timezone', { skipErrorToast: true });
    return res.data.data;
  },

  async getList(params: {
    page?: number;
    limit?: number;
    search?: string;
    healthStatus?: string;
    usageType?: string;
    timezone?: string;
    period?: HealthPeriod;
  }) {
    const q = new URLSearchParams();
    if (params.page) q.set('page', String(params.page));
    if (params.limit) q.set('limit', String(params.limit));
    if (params.search) q.set('search', params.search);
    if (params.healthStatus) q.set('healthStatus', params.healthStatus);
    if (params.usageType) q.set('usageType', params.usageType);
    if (params.timezone) q.set('timezone', params.timezone);
    if (params.period) q.set('period', params.period);
    const res = await apiClient.get(`/phone-numbers/health/list?${q}`, { skipErrorToast: true });
    return res.data.data as {
      items: PhoneHealthListItem[];
      period: HealthPeriod;
      periodLabel: string;
      pagination: { page: number; limit: number; total: number; pages: number };
    };
  },

  async getInventoryGaps(period: HealthPeriod = 'today'): Promise<AreaCodeGapRow[]> {
    const res = await apiClient.get<AreaCodeGapRow[]>(
      `/phone-numbers/inventory/gaps?period=${period}`,
      { skipErrorToast: true }
    );
    return res.data.data;
  },

  async getReplacementLog(limit = 50) {
    const res = await apiClient.get<ReplacementLogEntry[]>(
      `/phone-numbers/replacements/log?limit=${limit}`,
      { skipErrorToast: true }
    );
    return res.data.data;
  },

  async getSuggestions() {
    const res = await apiClient.get('/phone-numbers/replacements/suggestions', { skipErrorToast: true });
    return res.data.data;
  },

  async getAlerts() {
    const res = await apiClient.get('/phone-numbers/alerts');
    return res.data.data;
  },

  async setHealthStatus(id: string, status: string, reason?: string) {
    await apiClient.patch(
      `/phone-numbers/${id}/health-status`,
      { status, reason },
      { skipErrorToast: true }
    );
  },

  async unlockHealthStatus(id: string) {
    await apiClient.post(`/phone-numbers/${id}/health-unlock`);
  },

  async approveReplacement(suggestionId: string, campaignId?: string) {
    await apiClient.post(`/phone-numbers/replacements/${suggestionId}/approve`, { campaignId });
  },

  async rejectReplacement(suggestionId: string) {
    await apiClient.post(`/phone-numbers/replacements/${suggestionId}/reject`);
  },

  async recalculate() {
    const res = await apiClient.post('/phone-numbers/health/recalculate');
    return res.data.data;
  },

  async searchAvailable(areaCode: string) {
    const res = await apiClient.post('/phone-numbers/telnyx/search-available', { areaCode });
    return res.data.data;
  },

  async purchaseNumber(phoneNumber: string, connectionId?: string, messagingProfileId?: string) {
    const res = await apiClient.post('/phone-numbers/telnyx/purchase', {
      phoneNumber,
      connectionId,
      messagingProfileId,
    });
    return res.data.data;
  },
};
